import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import Register from "./pages/auth/registration";
import Login from "./pages/auth/login";
import MainPage from "./pages/Main";
import StatisticsPage from "./pages/SideBar/statistics";
import ConversionsPage from "./pages/SideBar/conversions";
import OffersPage from "./pages/SideBar/offers";
import StreamsPage from "./pages/SideBar/streams";
import PaymentsPage from "./pages/SideBar/payments";
import SettingsPage from "./pages/SideBar/settings";
import LogsPage from "./pages/SideBar/logs";
import HelpPage from "./pages/SideBar/help";
import ResetPassword from "./pages/auth/reset_password";
import AdminCabinetPage from "./pages/SideBar/admin/admin_cab";
import NotFoundPage from "./components/NotFoundPage";
import ReferralsPage from "./pages/SideBar/referrals";
import RefCapture from "./components/RefCapture";
import EmailVerifyPage from "./pages/auth/EmailVerifyPage";
import SupportPage from "./pages/SideBar/support";
import axios from "axios";

import "./styles/fl-app.css";
import "./styles/fl-pages.css";

const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const res = await originalFetch(...args);

  const url =
    typeof args[0] === "string"
      ? args[0]
      : args[0]?.url || "";

  const isAuthLogin = url.includes("/api/auth/login");

  if ((res.status === 401 || res.status === 403) && !isAuthLogin) {
    let reason = res.status === 401 ? "expired" : "forbidden";

    try {
      const clone = res.clone();
      const data = await clone.json().catch(() => null);

      if (res.status === 403 && (data?.error === "blocked" || data?.code === "blocked")) {
        reason = "blocked";
      }
    } catch (_) {}

    localStorage.removeItem("token");
    localStorage.removeItem("user_id");
    window.location.href = `/login?reason=${reason}`;

    throw new Error(
      res.status === 403 && reason === "blocked" ? "Blocked" : "Unauthorized"
    );
  }

  return res;
};

axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.error || err?.response?.data?.code;

    if (status === 401 || status === 403) {
      const reason =
        status === 403 && code === "blocked"
          ? "blocked"
          : status === 401
            ? "expired"
            : "forbidden";

      localStorage.removeItem("token");
      localStorage.removeItem("user_id");
      window.location.href = `/login?reason=${reason}`;
    }

    return Promise.reject(err);
  }
);

function AppRoutes() {
  const location = useLocation();

  const isDashboardRoute =
    location.pathname.startsWith("/statistics") ||
    location.pathname.startsWith("/conversions") ||
    location.pathname.startsWith("/offers") ||
    location.pathname.startsWith("/streams") ||
    location.pathname.startsWith("/payments") ||
    location.pathname.startsWith("/settings") ||
    location.pathname.startsWith("/logs") ||
    location.pathname.startsWith("/help") ||
    location.pathname.startsWith("/referrals") ||
    location.pathname.startsWith("/support") ||
    location.pathname.startsWith("/admin");

  const theme = document.documentElement.getAttribute("data-theme") || "light";

  return (
    <div className={isDashboardRoute ? "fl-app" : ""} data-theme={isDashboardRoute ? theme : undefined}>
      <RefCapture />
      <Routes>
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/statistics" element={<StatisticsPage />} />
        <Route path="/conversions" element={<ConversionsPage />} />
        <Route path="/offers" element={<OffersPage />} />
        <Route path="/streams" element={<StreamsPage />} />
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/logs" element={<LogsPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/referrals" element={<ReferralsPage />} />
        <Route path="/support" element={<SupportPage />} />
        <Route path="/" element={<MainPage />} />
        <Route path="/admin/*" element={<AdminCabinetPage />} />
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/verify-email" element={<EmailVerifyPage />} />
      </Routes>
    </div>
  );
}

const App = () => {
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        const errCode = error?.response?.data?.error;

        if (status === 403 && errCode === "blocked") {
          localStorage.removeItem("token");
          window.location.href = "/login?blocked=1";
        }

        return Promise.reject(error);
      }
    );

    return () => axios.interceptors.response.eject(id);
  }, []);

  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

export default App;
